import { useCallback, useEffect, useState } from "react";
import i18next from "src/i18n/config";

import { showErrorToast } from "@shared/toasts/Toasts";

import { fetchClientFeatures, FetchClientFeaturesParams } from "../api";

interface UseClientFeaturesState {
    data: API.Feature[] | null;
    loading: boolean;
}

export const useClientFeatures = (params: FetchClientFeaturesParams | null) => {
    const [state, setState] = useState<UseClientFeaturesState>({
        data: null,
        loading: false,
    });

    const fetchData = async () => {
        if (!params) {
            return;
        }

        setState((prev) => ({ ...prev, loading: true }));
        const response = await fetchClientFeatures({ ...params, includeUsage: true });
        setState({ data: response.model, loading: false });
    };

    useEffect(() => {
        if (!params) {
            setState({ data: null, loading: false });
            return;
        }

        const loadData = async () => {
            try {
                await fetchData();
            } catch (error) {
                setState((prev) => ({
                    ...prev,
                    loading: false,
                }));
                const err = error as Error;
                showErrorToast(err.message || i18next.t("pages:clientsOverviewPage.toast.fetchClientFeaturesError"));
            }
        };

        loadData();
    }, [params?.workspaceId, params?.clientId]);

    const refetch = useCallback(async () => {
        if (!params) {
            return;
        }

        try {
            await fetchData();
        } catch (error) {
            setState((prev) => ({
                ...prev,
                loading: false,
            }));
            const err = error as Error;
            showErrorToast(err.message || i18next.t("pages:clientsOverviewPage.toast.fetchClientFeaturesError"));
        }
    }, [params]);

    return {
        ...state,
        refetch,
    };
};
